import { Dispatch, useRef } from 'react';
import { Form, message, UploadFile, UploadProps } from 'antd';
import { useIntl } from 'react-intl';
import { StyledNewUploader, StyledUploader } from './style';
import { options } from '../../../config/config';
import ImgCrop from 'antd-img-crop';
import { RcFile } from 'antd/es/upload/interface';
import {
  flattenToJpeg,
  normalizeFileNames,
  TRANSPARENT_IMAGE_TYPES,
} from 'utils/imageHelpers';

interface UploaderProps {
  onFileListChange?: Dispatch<any>;
  fileList: UploadFile[];
  multiple?: boolean;
  handleRemove(file: UploadFile): void;
  isCrop?: boolean;
  aspectRatio?: number;
  selectedImageName?: string;
  setSelectedImageName?: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  selectable: boolean | undefined;
}

export const Uploader = ({
  onFileListChange,
  fileList,
  multiple = true,
  handleRemove,
  isCrop = true,
  aspectRatio,
  selectedImageName,
  setSelectedImageName,
  selectable,
}: UploaderProps) => {
  const intl = useIntl();

  const cancelledRef = useRef(false);
  // uids of JPEG/BMP originals that went through the cropper typed as PNG
  const opaqueOriginalUids = useRef(new Set<string>());

  const allowedTypes = options.images.allowedFormats;
  const maxImages = multiple ? options.images.maxImages : 1;

  const renderAllowedTypes = () => {
    const types = allowedTypes.map((type) => type.replace('image/', ''));
    return types.join(',');
  };

  const handleChange = (info: { file: UploadFile; fileList: UploadFile[] }) => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }

    let newList = [...info.fileList];
    newList = newList.filter((file) => {
      if (!file.originFileObj) {
        return true;
      }
      if (!file.url) {
        file.url = URL.createObjectURL(file.originFileObj);
      }
      const fileType = file.originFileObj.type;
      const isValidType = allowedTypes.includes(fileType || '');
      if (!isValidType) {
        message.error(
          `${intl.formatMessage({ id: 'images.allowed_types' })}${renderAllowedTypes()}`
        );
      }
      return isValidType;
    });

    if (maxImages && newList.length > maxImages) {
      message.warning(
        `${intl.formatMessage({ id: 'images.max_images' })}${maxImages}`
      );
      newList = newList.slice(0, maxImages);
    }

    newList = normalizeFileNames(newList);

    if (
      selectable &&
      setSelectedImageName &&
      selectedImageName?.startsWith('big_')
    ) {
      const actualName = selectedImageName.replace(/^([^_]+_\d+_)/, '');
      setSelectedImageName(actualName);
    }

    if (selectable && setSelectedImageName && selectedImageName === undefined) {
      setSelectedImageName(newList[0].name);
    }

    if (onFileListChange) {
      onFileListChange(newList);
    }
  };

  const props: UploadProps = {
    name: 'images',
    multiple: false,
    onChange: handleChange,
    fileList,
    listType: 'picture-card',
    showUploadList: false,
    onRemove: handleRemove,
    accept: allowedTypes.join(','),
    beforeUpload: async (file: RcFile) => {
      if (!opaqueOriginalUids.current.has(file.uid)) {
        return file;
      }
      opaqueOriginalUids.current.delete(file.uid);

      return flattenToJpeg(file);
    },
  };

  const beforeCrop = (file: RcFile) => {
    if (TRANSPARENT_IMAGE_TYPES.includes(file.type)) {
      return true;
    }
    opaqueOriginalUids.current.add(file.uid);
    const asPng = new File([file], file.name, { type: 'image/png' }) as RcFile;
    asPng.uid = file.uid;

    return asPng;
  };

  const wrapWithCrop = (children: any) => {
    if (isCrop) {
      return (
        <ImgCrop
          rotationSlider
          showReset
          aspect={aspectRatio}
          fillColor="transparent"
          quality={1}
          beforeCrop={beforeCrop}
          onModalCancel={() => {
            cancelledRef.current = true;
            opaqueOriginalUids.current.clear();
          }}
        >
          {children}
        </ImgCrop>
      );
    } else {
      return children;
    }
  };

  return (
    <StyledUploader>
      <Form.Item name={'images'}>
        {wrapWithCrop(
          <StyledNewUploader {...props}>
            <p className={'ant-upload-drag-icon'}>
              <img src={'/svgs/upload-image.svg'} alt="upload icon" />
            </p>
            <p className={'ant-upload-text'}>
              <span className={'gallery-first-text'}>
                {intl.formatMessage({ id: 'gallery.upload_file' })}{' '}
              </span>
              <div className={'gallery-second-text'}>
                {intl.formatMessage({ id: 'gallery.or_here' })}
              </div>
            </p>
          </StyledNewUploader>
        )}
      </Form.Item>
    </StyledUploader>
  );
};
